// GRN mockup (2026-10-22): "Print receipt slips" on Goods Receiving — every receipt of one day (today by default),
// one A4 slip per receipt, the same slip the receipt page prints (utils/grnSlip, decision g16).
import React, { useState } from 'react';
import { Button, DatePicker, Popover, message } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getGRN, listReceipts } from '../../api';
import { buildGrnSlipsHtml, GrnSlipData } from '../../utils/grnSlip';
import { useAuth } from '../../context/AuthContext';
import { OUTCOME_LABEL, PATH_LABEL, PAYMENT_LABEL } from './shared';

/** Slip data from a receipt as GET /api/grns/:grnId returns it. */
export function slipFromGrn(grn: any): GrnSlipData {
  const requesters = Array.from(new Set((grn.lines || []).map((l: any) => l.poLine?.mrLine?.mrHeader?.requestedBy?.displayName).filter(Boolean)));
  return {
    grnId: grn.grnId, poId: grn.poHeader?.poId || '', path: PATH_LABEL[grn.poHeader?.purchasePath] || null,
    vendor: grn.poHeader?.vendor?.name || '', department: grn.poHeader?.department?.name, receivedOn: grn.receivedOn,
    receivedBy: grn.receivedBy?.displayName, paidBy: grn.poHeader?.paymentMethod ? PAYMENT_LABEL[grn.poHeader.paymentMethod] : null,
    deliveryNote: grn.deliveryNoteRef, invoice: grn.supplierInvoice, notes: grn.notes, requester: requesters.join(', ') || null,
    lines: (grn.lines || []).map((l: any) => ({
      no: l.poLine?.lineNo, code: l.poLine?.mrLine?.catalogItem?.partNumber || l.poLine?.partNumber, item: l.poLine?.itemDescription || '',
      goesTo: l.poLine?.costCenter?.code || null, accepted: Number(l.acceptedQty), rejected: Number(l.rejectedQty), unit: l.poLine?.unit?.name,
      price: l.poLine?.actualUnitPrice != null ? Number(l.poLine.actualUnitPrice) : l.poLine?.unitPrice != null ? Number(l.poLine.unitPrice) : null,
      outcome: l.rejectOutcome ? OUTCOME_LABEL[l.rejectOutcome] || l.rejectOutcome : null,
    })),
  };
}

const PrintSlips: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState<Dayjs | null>(dayjs());
  const [busy, setBusy] = useState(false);

  const print = async () => {
    if (!day) return;
    // Open the window on the click itself, so pop-up blockers allow it; fill it once the slips are ready.
    const w = window.open('', '_blank');
    if (!w) { message.error('Allow pop-ups for this site to print'); return; }
    w.document.write('<p style="font-family:sans-serif">Preparing the receipt slips…</p>');
    setBusy(true);
    try {
      const d = day.format('YYYY-MM-DD');
      const list = await listReceipts({ startDate: d, endDate: d, limit: 200 });
      if (!list.data.length) { w.close(); message.info(`No receipts on ${day.format('D MMM YYYY')}`); return; }
      const grns = await Promise.all(list.data.map((r) => getGRN(r.grnId)));
      const html = buildGrnSlipsHtml(grns.map(slipFromGrn), { printedAt: dayjs().format('DD MMM YYYY [at] HH:mm'), printedBy: user?.displayName || '' });
      w.document.open(); w.document.write(html); w.document.close();
      setTimeout(() => w.print(), 300);
      setOpen(false);
    } catch (e: any) {
      w.close();
      message.error(e?.response?.data?.error || 'Could not prepare the slips');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      title="Print the receipt slips of"
      content={
        <div style={{ display: 'flex', gap: 8 }}>
          <DatePicker value={day} onChange={setDay} format="D MMM YYYY" allowClear={false} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          <Button type="primary" icon={<PrinterOutlined />} loading={busy} onClick={print}>Print</Button>
        </div>
      }
    >
      <Button icon={<PrinterOutlined />}>Print receipt slips</Button>
    </Popover>
  );
};

export default PrintSlips;
